import { css } from "@emotion/css";
import { Divider } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { GitHubAppAuthorizationStatus, GitHubAppAuthorizationStatusWithUserDetails, GitHubAppConnections, GitHubAppInstallations, Repository } from "@octopusdeploy/octopus-server-client";
import { OctopusError } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import { isGitHubAppTrustedFlow } from "~/areas/configuration/components/Git/getGitHubAppSettings";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { PageContent, type PageContentCalloutType } from "~/components/PageContent/PageContent";
import WarningPanel from "~/components/WarningPanel/WarningPanel";
import { GitConnectionAnalyticView } from "./Analytics/GitConnectionAnalyticView";
import GitHubAppConnectionList from "./GitHubAppConnectionList";
import { GitHubUserCard } from "./GitHubUserCard";
import { GitHubWebAppRegistrationCallout } from "./GitHubWebAppRegistrationCallout";
import { GitConnectionAuthorizeOnboarding, GitConnectionInstallOnboarding } from "./Onboarding/GitConnectionOnboarding";
import { ConfigureGitHubAppLink } from "./Onboarding/GitHubAppLinks";
export type GitHubNotConnectedInstallations = GitHubAppInstallations | undefined | "error";
export interface GitHubConnectionsPageLoaderData {
    connections: GitHubAppConnections;
    notConnectedInstallations: GitHubNotConnectedInstallations;
    authorizationStatus: GitHubAppAuthorizationStatus | GitHubAppAuthorizationStatusWithUserDetails;
}
interface GitHubConnectionsPageInternalProps {
    spaceId?: string;
    loaderData: GitHubConnectionsPageLoaderData;
}
export function GitHubConnectionsPageInternal({ spaceId, loaderData }: GitHubConnectionsPageInternalProps) {
    const { connections, notConnectedInstallations: initialNotConnectedInstallations, authorizationStatus: initialAuthorizationStatus } = loaderData;
    const [showRefreshCallout, setShowRefreshCallout] = useState(false);
    const { result: authorizationStatus, refetch: refetchAuthorizationStatus } = useQuery(async (repository) => {
        return await getAuthorizationStatus(repository);
    }, [], "Get authorization status for GitHub user", { initialResult: initialAuthorizationStatus });
    const { result: notConnectedInstallations } = useQuery(async (repository) => {
        return await getNotConnectedInstallations(repository, authorizationStatus.IsAuthorized);
    }, [authorizationStatus.IsAuthorized], "Get not connected installations for GitHub user", { initialResult: initialNotConnectedInstallations });
    const handleGitHubAppLinkClick = () => setShowRefreshCallout(!isGitHubAppTrustedFlow());
    const githubRefreshCallout: PageContentCalloutType | undefined = showRefreshCallout
        ? {
            type: "custom",
            content: <>{showRefreshCallout && <GitHubWebAppRegistrationCallout onClose={() => setShowRefreshCallout(false)}/>}</>,
        }
        : undefined;
    if (connections.Connections.length === 0) {
        // If we don't have any existing connections we want to show some sort of full-screen onboarding
        // because it's likely this is the first time they've hit this page. This covers all of the cases
        // where the generic list handling in the main return would leave users a little bit lost.
        if (!authorizationStatus.IsAuthorized) {
            // If we have no existing connections and the user is not authorized, show the authorize onboarding.
            return <GitConnectionAuthorizeOnboarding />;
        }
        if (notConnectedInstallations === undefined) {
            // If we have no connections, and the user is authorized but haven't finished loading the installations
            // from GitHub, don't show anything yet (unless there's an error to display).
            return null;
        }
    }
    const isInSpaceContext = spaceId !== undefined; // Temporary workaround while Platform Hub GitHub connections is in development
    return (<PageContent header={{ title: "GitHub Connections" }} callout={githubRefreshCallout}>
            {notConnectedInstallations === "error" && <InstallationWarning />}
            <GitHubUserSection authorizationStatus={authorizationStatus} refetchAuthorizationStatus={refetchAuthorizationStatus}/>
            {/* TODO: modelling-at-scale - Decouple GitHubConnectionsSection from spaceId. For now, hide this section in Platform Hub. */}
            {isInSpaceContext && (<GitHubConnectionsSection spaceId={spaceId} connections={connections} notConnectedInstallations={notConnectedInstallations} isAuthorized={authorizationStatus.IsAuthorized} handleGitHubAppLinkClick={handleGitHubAppLinkClick}/>)}
        </PageContent>);
}
export async function getNotConnectedInstallations(repository: Repository, isAuthorized: boolean): Promise<GitHubNotConnectedInstallations> {
    if (isAuthorized) {
        try {
            return await repository.GitHubApp.getInstallations(true); // `true` skips any installations that are already connected.
        }
        catch (error) {
            if (error instanceof OctopusError) {
                return "error";
            }
            throw error; // Re-throw other errors
        }
    }
    else {
        return undefined;
    }
}
export async function getAuthorizationStatus(repository: Repository): Promise<GitHubAppAuthorizationStatus | GitHubAppAuthorizationStatusWithUserDetails> {
    return await repository.GitHubApp.getAuthorizationStatusWithUserDetails();
}
interface GitHubUserSectionProps {
    authorizationStatus: GitHubAppAuthorizationStatus | GitHubAppAuthorizationStatusWithUserDetails;
    refetchAuthorizationStatus: () => void;
}
function GitHubUserSection({ authorizationStatus, refetchAuthorizationStatus }: GitHubUserSectionProps) {
    return (<>
            <h2>Current User</h2>
            <GitHubUserCard authorizationStatus={authorizationStatus} onUserUpdated={refetchAuthorizationStatus}/>
        </>);
}
interface GitHubConnectionsSectionProps {
    spaceId: string;
    connections: GitHubAppConnections;
    notConnectedInstallations: GitHubNotConnectedInstallations;
    isAuthorized: boolean;
    handleGitHubAppLinkClick: () => void;
}
function GitHubConnectionsSection({ spaceId, connections, notConnectedInstallations, isAuthorized, handleGitHubAppLinkClick }: GitHubConnectionsSectionProps) {
    // If we have no connections or installations, show the installation onboarding
    if (connections.Connections.length === 0 && notConnectedInstallations !== "error" && notConnectedInstallations?.Installations.length === 0) {
        return (<>
                <h2>Connections</h2>
                <BorderedContent>
                    <GitConnectionInstallOnboarding onExternalLinkClick={handleGitHubAppLinkClick}/>
                </BorderedContent>
            </>);
    }
    return (<>
            <h2>Connections</h2>
            <BorderedContent>
                <GitConnectionAnalyticView name="View GitHub connections list"/>
                <GitHubAppConnectionList spaceId={spaceId} connections={connections} notConnectedInstallations={notConnectedInstallations === "error" ? undefined : notConnectedInstallations}/>
                <GitConnectionsInstallFooter userIsAuthorized={isAuthorized} onGitHubAppLinkClick={handleGitHubAppLinkClick}/>
            </BorderedContent>
        </>);
}
function InstallationWarning() {
    return <WarningPanel title="GitHub installations error" warnings={["We couldn't retrieve your GitHub installations. Please re-authorize your GitHub account."]}/>;
}
interface GitConnectionsInstallFooterProps {
    userIsAuthorized: boolean;
    onGitHubAppLinkClick: () => void;
}
function GitConnectionsInstallFooter({ userIsAuthorized, onGitHubAppLinkClick }: GitConnectionsInstallFooterProps) {
    const styles = {
        footer: css({
            marginLeft: space[16],
            marginRight: space[16],
            marginTop: space[24],
            marginBottom: space[24],
            textAlign: "center",
        }),
    };
    if (!userIsAuthorized) {
        return null;
    }
    return (<>
            <Divider />
            <div className={styles.footer}>
                <p>Install the GitHub app to connect to other organizations or users</p>
                <p>
                    <ConfigureGitHubAppLink onClick={onGitHubAppLinkClick}/>
                </p>
            </div>
        </>);
}
